<script setup>
import { computed, nextTick, onBeforeUnmount, onMounted, ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { Modal } from 'bootstrap';
import { useUsuarioStore } from '@/store/usuarioStore';
import { useListadosEntidadesStore } from '@/store/listadosEntidadesStore';
import usuarioModel from '@/models/usuarioModel';
import { showAlert } from '@/utils/alerts';
import { convertToBase64File } from '@/utils/helpers';
import { attachPlacesAutocomplete, matchProvinciaIdByName, parsePlaceAddress } from '@/utils/googlePlaces';

const router = useRouter();
const route = useRoute();
const usuarioStore = useUsuarioStore();
const listadosEntidadesStore = useListadosEntidadesStore();

const MAX_FACTURACION_PERFILES = 5;

const tipo = ref(route.query?.tipo === 'responsable' ? 'responsable' : 'consumidor');

const provincias = computed(() => listadosEntidadesStore?.provincias ?? []);

const perfilId = computed(() => {
  const raw = route.query?.id;
  if (raw == null || raw === '') return null;
  const num = Number(raw);
  return Number.isFinite(num) ? num : null;
});

const isEdit = computed(() => perfilId.value != null);

const toNullableNumber = (value) => {
  if (value == null || value === '') return null;
  const onlyDigits = String(value).replaceAll(/\D+/g, '');
  if (!onlyDigits) return null;
  const num = Number(onlyDigits);
  return Number.isFinite(num) ? num : null;
};

const normalizeOptionalString = (value) => {
  if (value == null) return null;
  const text = String(value).trim();
  return text === '' ? null : text;
};

const formatCuitCuil = (value) => {
  const digits = String(value ?? '').replaceAll(/\D+/g, '').slice(0, 11);
  const part1 = digits.slice(0, 2);
  const part2 = digits.slice(2, 10);
  const part3 = digits.slice(10, 11);

  if (!part1) return '';
  if (!part2) return part1;
  if (!part3) return `${part1}-${part2}`;
  return `${part1}-${part2}-${part3}`;
};

const onCuitInput = (e) => {
  const el = e?.target;
  const next = formatCuitCuil(el?.value);
  form.value.cuit = next;

  // Mantener UX simple: cursor al final (evita saltos raros con el formateo)
  if (el && typeof el.setSelectionRange === 'function') {
    requestAnimationFrame(() => {
      try {
        el.setSelectionRange(next.length, next.length);
      } catch {
        // ignore
      }
    });
  }
};

const fetchFacturacionProfiles = async () => {
  const res = await usuarioModel.obtenerPerfilesFacturacionUsuario();
  const perfiles = res?.data ?? res;

  let list = [];
  if (Array.isArray(perfiles)) list = perfiles;
  else if (Array.isArray(perfiles?.data)) list = perfiles.data;
  return list;
};

const ensureCanCreateFacturacionProfile = async () => {
  const perfiles = await fetchFacturacionProfiles();
  if (perfiles.length >= MAX_FACTURACION_PERFILES) {
    showAlert('danger', `Solo podés tener hasta ${MAX_FACTURACION_PERFILES} perfiles de facturación.`);
    return false;
  }
  return true;
};

const includeKeysByTipo = (tipoIva) => {
  const keys = ['constancia_cuit'];
  if (tipoIva === 'consumidor') keys.push('dni_frente', 'dni_dorso');
  else keys.push('cm05', 'ingresos_brutos');
  return keys;
};

const labelByKeyForTipo = (tipoIva) => ({
  constancia_cuit: tipoIva === 'consumidor' ? 'Constancia de Cuil' : 'Constancia de Cuit',
  dni_frente: 'Foto del frente de DNI',
  dni_dorso: 'Foto del dorso de DNI',
  cm05: 'CM05',
  ingresos_brutos: 'Ingresos brutos',
});

const buildFacturacionPayload = async () => {
  const payload = {
    telefono_fijo: toNullableNumber(form.value.telefono_fijo),
    telefono_celular: toNullableNumber(form.value.telefono_celular),
    cuit_cuil: toNullableNumber(form.value.cuit),
    razon_social: normalizeOptionalString(form.value.nombre_empresa),
    calle_numero: normalizeOptionalString(form.value.direccion),
    localidad: normalizeOptionalString(form.value.localidad),
    codigo_postal: normalizeOptionalString(form.value.codigo_postal),
    provincia_id: form.value.provincia_id === '' ? null : Number(form.value.provincia_id),
    predeterminada: Boolean(form.value.isPredeterminada),
  };

  const filesSelected = form.value.archivos ?? {};
  const includeKeys = includeKeysByTipo(tipo.value);
  const labelByKey = labelByKeyForTipo(tipo.value);

  for (const key of includeKeys) {
    const file = filesSelected?.[key] ?? null;
    if (!file) continue;
    if (file.size > MAX_FILE_BYTES) {
      showAlert('danger', `El archivo "${labelByKey[key] ?? key}" supera el peso máximo de 2MB.`);
      return null;
    }

    const backendKey = uiKeyToBackendKey(key);
    const archivoPayload = await archivoPayloadFor(backendKey, file);
    if (!archivoPayload) {
      showAlert('danger', `No se pudo preparar el archivo "${labelByKey[key] ?? key}" para enviar. Verificá formato (pdf/png/jpg/jpeg/webp).`);
      return null;
    }

    payload[backendKey] = archivoPayload;
  }

  return payload;
};

const fileInputRefs = {
  constancia_cuit: ref(null),
  dni_frente: ref(null),
  dni_dorso: ref(null),
  cm05: ref(null),
  ingresos_brutos: ref(null),
};

const direccionInputEl = ref(null);
const cleanupPlaces = ref(() => {});

const deleteModalEl = ref(null);
const deleteModal = ref(null);
const isDeleting = ref(false);
const navigateAfterDelete = ref(false);
const onDeleteModalHidden = () => {
  if (!navigateAfterDelete.value) return;
  navigateAfterDelete.value = false;
  router.push({ name: 'Perfil' });
};

const existingArchivos = ref({
  constrancia_cuil: { has: false, name: '' },
  constancia_cuit: { has: false, name: '' },
  dni_frente: { has: false, name: '' },
  dni_dorso: { has: false, name: '' },
  cm05: { has: false, name: '' },
  iibb: { has: false, name: '' },
});

const fileNameFromObj = (obj) => {
  if (!obj || typeof obj !== 'object') return '';
  return (
    obj.nombre ??
    obj.nombre_archivo ??
    obj.filename ??
    obj.original_name ??
    obj.originalName ??
    obj.name ??
    ''
  );
};

const buildExistingFile = ({ id, obj }) => {
  const has = id != null && id !== '';
  const name = String(fileNameFromObj(obj) || '').trim();
  return { has, name: has ? (name || 'Nombredelarchivo.jpg') : '' };
};

const setExistingFromPerfil = (perfil) => {
  const p = perfil ?? {};

  const cuilId = p.constrancia_cuil_archivo_id ?? p.constancia_cuil_archivo_id;
  const cuilObj = p.constrancia_cuil_archivo ?? p.constancia_cuil_archivo;

  existingArchivos.value = {
    constrancia_cuil: buildExistingFile({ id: cuilId, obj: cuilObj }),
    constancia_cuit: buildExistingFile({ id: p.constancia_cuit_archivo_id, obj: p.constancia_cuit_archivo }),
    dni_frente: buildExistingFile({ id: p.dni_frente_archivo_id, obj: p.dni_frente_archivo }),
    dni_dorso: buildExistingFile({ id: p.dni_dorso_archivo_id, obj: p.dni_dorso_archivo }),
    cm05: buildExistingFile({ id: p.cm05_archivo_id, obj: p.cm05_archivo }),
    iibb: buildExistingFile({ id: p.iibb_archivo_id, obj: p.iibb_archivo }),
  };
};

const inferTipoFromPerfil = (perfil) => {
  const p = perfil ?? {};
  const hasConsumidor =
    p.constrancia_cuil_archivo_id != null ||
    p.constancia_cuil_archivo_id != null ||
    p.dni_frente_archivo_id != null ||
    p.dni_dorso_archivo_id != null;

  const hasResponsable =
    p.constancia_cuit_archivo_id != null ||
    p.cm05_archivo_id != null ||
    p.iibb_archivo_id != null;

  if (hasConsumidor && !hasResponsable) return 'consumidor';
  if (hasResponsable && !hasConsumidor) return 'responsable';

  const raw = p.tipo ?? p.condicion_iva ?? p.condicion_frente_iva ?? p.condicion_iva_nombre ?? p.iva;
  const text = (raw == null ? '' : String(raw)).toLowerCase();
  if (text.includes('responsable') || text.includes('inscrip')) return 'responsable';
  if (text.includes('consumidor')) return 'consumidor';
  return null;
};

const uiKeyToBackendKey = (uiKey) => {
  if (uiKey === 'ingresos_brutos') return 'iibb';
  if (uiKey === 'constancia_cuit' && tipo.value === 'consumidor') return 'constrancia_cuil';
  return uiKey;
};

const fileExtension = (file) => {
  const name = file?.name ? String(file.name) : '';
  const dot = name.lastIndexOf('.');
  const extFromName = dot >= 0 ? name.slice(dot + 1).toLowerCase() : '';
  if (extFromName) return extFromName;

  const mime = (file?.type ?? '').toLowerCase();
  if (mime.includes('pdf')) return 'pdf';
  if (mime.includes('jpeg')) return 'jpeg';
  if (mime.includes('jpg')) return 'jpg';
  if (mime.includes('png')) return 'png';
  if (mime.includes('webp')) return 'webp';
  return '';
};

const extractBase64 = (dataUrlOrBase64) => {
  if (!dataUrlOrBase64 || typeof dataUrlOrBase64 !== 'string') return '';
  const comma = dataUrlOrBase64.indexOf(',');
  if (comma === -1) return dataUrlOrBase64;
  return dataUrlOrBase64.slice(comma + 1);
};

const archivoPayloadFor = async (backendKey, file) => {
  if (!file) return null;

  const ext = fileExtension(file);
  if (!ext) return null;

  // Regla pedida: pdf => tipo_archivo_id = 5, imagen => tipo_archivo_id = 1
  // (aplica a todos los archivos del perfil de facturación)
  const isPdf = ext === 'pdf';
  const isImage = ['png', 'jpg', 'jpeg', 'webp'].includes(ext);
  if (!isPdf && !isImage) return null;
  const tipoId = isPdf ? 5 : 1;

  const dataUrl = await convertToBase64File(file);
  const base64 = extractBase64(dataUrl);
  if (!base64) return null;

  return {
    archivo: base64,
    extension: ext,
    tipo_archivo_id: tipoId,
  };
};

const form = ref({
  tipo: tipo.value,
  nombre_empresa: '',
  cuit: '',
  direccion: '',
  localidad: '',
  telefono_fijo: '',
  telefono_celular: '',
  codigo_postal: '',
  provincia_id: '',
  isPredeterminada: false,
  archivos: {
    constancia_cuit: null,
    dni_frente: null,
    dni_dorso: null
    ,cm05: null,
    ingresos_brutos: null
  }
});

onMounted(async () => {
  try {
    await usuarioStore.getSession();
    await listadosEntidadesStore.getProvincias();

    if (isEdit.value) {
      const res = await usuarioModel.obtenerPerfilesFacturacionUsuario();
      const perfiles = res?.data ?? res;
      let list = [];
      if (Array.isArray(perfiles)) list = perfiles;
      else if (Array.isArray(perfiles?.data)) list = perfiles.data;
      const current = list.find((p) => Number(p.id) === Number(perfilId.value));
      if (current) {
        const inferred = inferTipoFromPerfil(current);
        const nextTipo = inferred || (route.query?.tipo === 'responsable' ? 'responsable' : 'consumidor');
        tipo.value = nextTipo;
        form.value.tipo = nextTipo;
        router.replace({ query: { ...route.query, tipo: nextTipo } });

        setExistingFromPerfil(current);

        form.value.nombre_empresa = current.razon_social ?? '';
        form.value.cuit = formatCuitCuil(current.cuit_cuil ?? '');
        form.value.direccion = current.calle_numero ?? '';
        form.value.localidad = current.localidad ?? '';
        form.value.telefono_fijo = current.telefono_fijo ?? '';
        form.value.telefono_celular = current.telefono_celular ?? '';
        form.value.codigo_postal = current.codigo_postal ?? '';
        form.value.provincia_id = current.provincia_id ?? '';
        form.value.isPredeterminada = Boolean(current.predeterminada);
      }
    }

    await nextTick();
    const apiKey = process.env.VUE_APP_API_KEY_GMAPS
    cleanupPlaces.value = await attachPlacesAutocomplete({
      inputEl: direccionInputEl.value,
      apiKey,
      country: 'ar',
      onSelect: (place) => {
        const parsed = parsePlaceAddress(place);
        const direccion = parsed?.direccion || place?.name || place?.formatted_address || '';
        if (direccion) form.value.direccion = direccion;
        if (parsed?.codigo_postal) form.value.codigo_postal = parsed.codigo_postal;
        if (parsed?.localidad) form.value.localidad = parsed.localidad;

        const provinciaId = matchProvinciaIdByName(provincias.value, parsed?.provincia);
        if (provinciaId != null) form.value.provincia_id = provinciaId;
      },
    });

    if (deleteModalEl.value) {
      deleteModal.value = new Modal(deleteModalEl.value, { backdrop: 'static', keyboard: false });
      deleteModalEl.value.addEventListener('hidden.bs.modal', onDeleteModalHidden);
    }
  } catch (e) {
    console.error('Error cargando contexto de facturación', e);
  }
});

onBeforeUnmount(() => {
  try {
    if (typeof cleanupPlaces.value === 'function') cleanupPlaces.value();
  } catch (e) {
    console.debug('[places] cleanup falló', e);
  }

  try {
    if (deleteModalEl.value) {
      deleteModalEl.value.removeEventListener('hidden.bs.modal', onDeleteModalHidden);
    }

    // No llamamos dispose(): si el modal está en transición puede romper (Bootstrap usa transition end)
    if (deleteModal.value) deleteModal.value.hide();
  } catch {
    // ignore
  }
});

const setTipo = (nextTipo) => {
  tipo.value = nextTipo;
  form.value.tipo = nextTipo;
  router.replace({ query: { ...route.query, tipo: nextTipo } });
};

const onFileChange = (key, event) => {
  const file = event?.target?.files?.[0] ?? null;
  form.value.archivos[key] = file;
};

const fileName = (key) => {
  const selected = form.value.archivos?.[key] ?? null;
  if (selected?.name) return selected.name;
  const backendKey = uiKeyToBackendKey(key);
  return existingArchivos.value?.[backendKey]?.name ?? '';
};

const MAX_FILE_BYTES = 2 * 1024 * 1024;

const getFileStatus = (key) => {
  const file = form.value.archivos?.[key] ?? null;
  if (!file) {
    const backendKey = uiKeyToBackendKey(key);
    const hasExisting = Boolean(existingArchivos.value?.[backendKey]?.has);
    if (hasExisting) return { state: 'success', message: 'Archivo subido correctamente' };
    return { state: 'empty', message: '' };
  }
  if (file.size > MAX_FILE_BYTES) return { state: 'error', message: 'Error al cargar archivo. Peso máximo 2MB.' };
  return { state: 'success', message: 'Archivo subido correctamente' };
};

const triggerFile = (key) => {
  const el = fileInputRefs?.[key]?.value;
  if (el) {
    // Permite re-seleccionar el mismo archivo y que dispare el evento change.
    try {
      el.value = '';
    } catch {
      // ignore
    }
    if (typeof el.click === 'function') el.click();
  }
};

const handleCancel = () => {
  router.push({ name: 'Perfil' });
};

const openDeleteModal = () => {
  if (!isEdit.value) return;
  if (deleteModal.value) deleteModal.value.show();
};

const closeDeleteModal = () => {
  if (deleteModal.value) deleteModal.value.hide();
};

const confirmDelete = async () => {
  if (!isEdit.value) return;
  if (isDeleting.value) return;
  isDeleting.value = true;

  try {
    await usuarioModel.eliminarPerfilFacturacion(perfilId.value);
    showAlert('success', 'Perfil de facturación eliminado.');

    // Es importante esperar a que el modal termine de ocultarse antes de navegar,
    // para evitar errores de Bootstrap durante la transición.
    if (deleteModal.value) {
      navigateAfterDelete.value = true;
      closeDeleteModal();
    } else {
      router.push({ name: 'Perfil' });
    }
  } catch (error) {
    const message =
      error?.response?.data?.message ||
      error?.response?.data?.error ||
      'No se pudo eliminar el perfil de facturación.';
    showAlert('danger', message);
  } finally {
    isDeleting.value = false;
  }
};

const handleSave = async () => {
  try {
    if (!isEdit.value) {
      const ok = await ensureCanCreateFacturacionProfile();
      if (!ok) return;
    }

    const payload = await buildFacturacionPayload();
    if (!payload) return;

    if (isEdit.value) {
      await usuarioModel.editarPerfilFacturacion(perfilId.value, payload);
      showAlert('success', 'Perfil de facturación actualizado.');
    } else {
      await usuarioModel.crearPerfilFacturacion(payload);
      showAlert('success', 'Perfil de facturación creado.');
    }

    router.push({ name: 'Perfil' });
  } catch (error) {
    const message = error?.response?.data?.message || error?.response?.data?.error || 'No se pudo guardar el perfil de facturación.';
    showAlert('danger', message);
  }
};
</script>

<template>
  <div class="facturacion-nuevo-page">
    <div class="container mt-5 mb-5">
      <h1 class="text-center fw-medium mb-5" style="font-size: 35px;">{{ isEdit ? 'Editar perfil' : 'Crear perfil' }}</h1>

      <div class="facturacion-card">
        <div class="iva-block">
          <p class="iva-title">Condición frente al IVA</p>
          <div class="iva-options">
            <label class="iva-option">
              <input
                class="iva-radio"
                type="radio"
                name="iva"
                value="consumidor"
                v-model="tipo"
                @change="setTipo('consumidor')"
              />
              <span class="iva-tag iva-tag--consumidor" :class="{ active: tipo === 'consumidor' }">CONSUMIDOR FINAL</span>
            </label>
            <label class="iva-option">
              <input
                class="iva-radio"
                type="radio"
                name="iva"
                value="responsable"
                v-model="tipo"
                @change="setTipo('responsable')"
              />
              <span class="iva-tag iva-tag--responsable" :class="{ active: tipo === 'responsable' }">RESPONSABLE INSCRIPTO</span>
            </label>
          </div>
        </div>

        <div class="grid-row two-col">
          <div class="field">
            <label class="form-label fw-bold" for="fact-nombre">{{ tipo === 'responsable' ? 'Nombre de la empresa' : 'Nombre completo' }}</label>
            <input id="fact-nombre" v-model="form.nombre_empresa" type="text" class="form-control custom-input" placeholder="" />
          </div>
          <div class="field">
            <label class="form-label fw-bold" for="fact-direccion">Dirección</label>
            <input
              id="fact-direccion"
              ref="direccionInputEl"
              v-model="form.direccion"
              type="text"
              class="form-control custom-input"
              placeholder=""
              autocomplete="off"
            />
          </div>
        </div>

        <div class="grid-row two-col">
          <div class="field">
            <label class="form-label fw-bold" for="fact-cuit">{{ tipo === 'consumidor' ? 'CUIL' : 'CUIT' }}</label>
            <input
              id="fact-cuit"
              v-model="form.cuit"
              type="text"
              class="form-control custom-input"
              placeholder="00-00000000-0"
              inputmode="numeric"
              autocomplete="off"
              maxlength="13"
              @input="onCuitInput"
            />
          </div>

          <div class="right-group">
            <div class="field">
              <label class="form-label fw-bold" for="fact-localidad">Localidad</label>
              <input id="fact-localidad" v-model="form.localidad" type="text" class="form-control custom-input" placeholder="" />
            </div>
            <div class="field">
              <label class="form-label fw-bold" for="fact-cp">Código Postal</label>
              <input id="fact-cp" v-model="form.codigo_postal" type="text" class="form-control custom-input" placeholder="" />
            </div>
          </div>
        </div>

        <div class="grid-row two-col">
          <div class="left-group">
            <div class="field">
              <label class="form-label fw-bold" for="fact-tel-fijo">Teléfono fijo</label>
              <input id="fact-tel-fijo" v-model="form.telefono_fijo" type="text" class="form-control custom-input" placeholder="" />
            </div>
            <div class="field">
              <label class="form-label fw-bold" for="fact-tel-cel">Teléfono celular</label>
              <input id="fact-tel-cel" v-model="form.telefono_celular" type="text" class="form-control custom-input" placeholder="" />
            </div>
          </div>

          <div class="field">
            <label class="form-label fw-bold" for="fact-provincia">Provincia</label>
            <select id="fact-provincia" v-model="form.provincia_id" class="form-select custom-input">
              <option value="" disabled>Seleccionar</option>
              <option v-for="p in provincias" :key="p.id" :value="p.id">
                {{ p.nombre }}
              </option>
            </select>
          </div>
        </div>

        <div class="files-block">
          <div class="file-item">
            <div class="form-label fw-bold">*{{ tipo === 'consumidor' ? 'Constancia de Cuil' : 'Constancia de Cuit' }}</div>
            <div class="file-row">
              <div class="file-field">
                <input class="file-name" type="text" :value="fileName('constancia_cuit') || 'Nombredelarchivo.jpg'" readonly />
                <button class="file-btn" type="button" @click="triggerFile('constancia_cuit')">Seleccionar</button>
                <input
                  id="file-constancia-cuit"
                  :ref="(el) => (fileInputRefs.constancia_cuit.value = el)"
                  class="d-none"
                  type="file"
                  accept=".jpg,.jpeg,.png,.pdf"
                  @change="(e) => onFileChange('constancia_cuit', e)"
                />
              </div>
              <div v-if="getFileStatus('constancia_cuit').state !== 'empty'" class="file-status" :class="getFileStatus('constancia_cuit').state">
                <svg v-if="getFileStatus('constancia_cuit').state === 'success'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                  <path fill-rule="evenodd" clip-rule="evenodd" d="M7 14C10.8661 14 14 10.8661 14 7C14 3.1339 10.8661 0 7 0C3.1339 0 0 3.1339 0 7C0 10.8661 3.1339 14 7 14ZM6.81956 9.83111L10.7084 5.16444L9.51378 4.16889L6.16933 8.18144L4.43878 6.45011L3.339 7.54989L5.67233 9.88322L6.27433 10.4852L6.81956 9.83111Z" fill="currentColor"/>
                </svg>
                <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                  <path d="M7 0C10.8661 0 14 3.1339 14 7C14 10.8661 10.8661 14 7 14C3.1339 14 0 10.8661 0 7C0 3.1339 3.1339 0 7 0ZM7 9.1C6.81435 9.1 6.6363 9.17375 6.50503 9.30503C6.37375 9.4363 6.3 9.61435 6.3 9.8C6.3 9.98565 6.37375 10.1637 6.50503 10.295C6.6363 10.4263 6.81435 10.5 7 10.5C7.18565 10.5 7.3637 10.4263 7.49497 10.295C7.62625 10.1637 7.7 9.98565 7.7 9.8C7.7 9.61435 7.62625 9.4363 7.49497 9.30503C7.3637 9.17375 7.18565 9.1 7 9.1ZM7 2.8C6.82855 2.80002 6.66306 2.86297 6.53494 2.9769C6.40681 3.09083 6.32496 3.24782 6.3049 3.4181L6.3 3.5V7.7C6.3002 7.87842 6.36852 8.05002 6.49099 8.17976C6.61347 8.30949 6.78087 8.38756 6.95898 8.39802C7.13709 8.40848 7.31247 8.35053 7.44928 8.23601C7.5861 8.1215 7.67403 7.95907 7.6951 7.7819L7.7 7.7V3.5C7.7 3.31435 7.62625 3.1363 7.49497 3.00503C7.3637 2.87375 7.18565 2.8 7 2.8Z" fill="currentColor"/>
                </svg>
                <span class="status-text">{{ getFileStatus('constancia_cuit').message }}</span>
              </div>
            </div>
          </div>

          <template v-if="tipo === 'consumidor'">
            <div class="file-item">
              <div class="form-label fw-bold">*Foto del frente de DNI</div>
              <div class="file-row">
                <div class="file-field">
                  <input class="file-name" type="text" :value="fileName('dni_frente') || 'Nombredelarchivo.jpg'" readonly />
                  <button class="file-btn" type="button" @click="triggerFile('dni_frente')">Seleccionar</button>
                  <input
                    id="file-dni-frente"
                    :ref="(el) => (fileInputRefs.dni_frente.value = el)"
                    class="d-none"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    @change="(e) => onFileChange('dni_frente', e)"
                  />
                </div>
                <div v-if="getFileStatus('dni_frente').state !== 'empty'" class="file-status" :class="getFileStatus('dni_frente').state">
                  <svg v-if="getFileStatus('dni_frente').state === 'success'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7 14C10.8661 14 14 10.8661 14 7C14 3.1339 10.8661 0 7 0C3.1339 0 0 3.1339 0 7C0 10.8661 3.1339 14 7 14ZM6.81956 9.83111L10.7084 5.16444L9.51378 4.16889L6.16933 8.18144L4.43878 6.45011L3.339 7.54989L5.67233 9.88322L6.27433 10.4852L6.81956 9.83111Z" fill="currentColor"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M7 0C10.8661 0 14 3.1339 14 7C14 10.8661 10.8661 14 7 14C3.1339 14 0 10.8661 0 7C0 3.1339 3.1339 0 7 0ZM7 9.1C6.81435 9.1 6.6363 9.17375 6.50503 9.30503C6.37375 9.4363 6.3 9.61435 6.3 9.8C6.3 9.98565 6.37375 10.1637 6.50503 10.295C6.6363 10.4263 6.81435 10.5 7 10.5C7.18565 10.5 7.3637 10.4263 7.49497 10.295C7.62625 10.1637 7.7 9.98565 7.7 9.8C7.7 9.61435 7.62625 9.4363 7.49497 9.30503C7.3637 9.17375 7.18565 9.1 7 9.1ZM7 2.8C6.82855 2.80002 6.66306 2.86297 6.53494 2.9769C6.40681 3.09083 6.32496 3.24782 6.3049 3.4181L6.3 3.5V7.7C6.3002 7.87842 6.36852 8.05002 6.49099 8.17976C6.61347 8.30949 6.78087 8.38756 6.95898 8.39802C7.13709 8.40848 7.31247 8.35053 7.44928 8.23601C7.5861 8.1215 7.67403 7.95907 7.6951 7.7819L7.7 7.7V3.5C7.7 3.31435 7.62625 3.1363 7.49497 3.00503C7.3637 2.87375 7.18565 2.8 7 2.8Z" fill="#FF4D4D"/>
</svg>
                  <span class="status-text">{{ getFileStatus('dni_frente').message }}</span>
                </div>
              </div>
            </div>

            <div class="file-item">
              <div class="form-label fw-bold">*Foto del dorso de DNI</div>
              <div class="file-row">
                <div class="file-field">
                  <input class="file-name" type="text" :value="fileName('dni_dorso') || 'Nombredelarchivo.jpg'" readonly />
                  <button class="file-btn" type="button" @click="triggerFile('dni_dorso')">Seleccionar</button>
                  <input
                    id="file-dni-dorso"
                    :ref="(el) => (fileInputRefs.dni_dorso.value = el)"
                    class="d-none"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    @change="(e) => onFileChange('dni_dorso', e)"
                  />
                </div>
                <div v-if="getFileStatus('dni_dorso').state !== 'empty'" class="file-status" :class="getFileStatus('dni_dorso').state">
                  <svg v-if="getFileStatus('dni_dorso').state === 'success'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7 14C10.8661 14 14 10.8661 14 7C14 3.1339 10.8661 0 7 0C3.1339 0 0 3.1339 0 7C0 10.8661 3.1339 14 7 14ZM6.81956 9.83111L10.7084 5.16444L9.51378 4.16889L6.16933 8.18144L4.43878 6.45011L3.339 7.54989L5.67233 9.88322L6.27433 10.4852L6.81956 9.83111Z" fill="currentColor"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M7 0C10.8661 0 14 3.1339 14 7C14 10.8661 10.8661 14 7 14C3.1339 14 0 10.8661 0 7C0 3.1339 3.1339 0 7 0ZM7 9.1C6.81435 9.1 6.6363 9.17375 6.50503 9.30503C6.37375 9.4363 6.3 9.61435 6.3 9.8C6.3 9.98565 6.37375 10.1637 6.50503 10.295C6.6363 10.4263 6.81435 10.5 7 10.5C7.18565 10.5 7.3637 10.4263 7.49497 10.295C7.62625 10.1637 7.7 9.98565 7.7 9.8C7.7 9.61435 7.62625 9.4363 7.49497 9.30503C7.3637 9.17375 7.18565 9.1 7 9.1ZM7 2.8C6.82855 2.80002 6.66306 2.86297 6.53494 2.9769C6.40681 3.09083 6.32496 3.24782 6.3049 3.4181L6.3 3.5V7.7C6.3002 7.87842 6.36852 8.05002 6.49099 8.17976C6.61347 8.30949 6.78087 8.38756 6.95898 8.39802C7.13709 8.40848 7.31247 8.35053 7.44928 8.23601C7.5861 8.1215 7.67403 7.95907 7.6951 7.7819L7.7 7.7V3.5C7.7 3.31435 7.62625 3.1363 7.49497 3.00503C7.3637 2.87375 7.18565 2.8 7 2.8Z" fill="#FF4D4D"/>
</svg>
                  <span class="status-text">{{ getFileStatus('dni_dorso').message }}</span>
                </div>
              </div>
            </div>
          </template>

          <template v-else>
            <div class="file-item">
              <div class="form-label fw-bold">*CM05</div>
              <div class="file-row">
                <div class="file-field">
                  <input class="file-name" type="text" :value="fileName('cm05') || 'Nombredelarchivo.jpg'" readonly />
                  <button class="file-btn" type="button" @click="triggerFile('cm05')">Seleccionar</button>
                  <input
                    id="file-cm05"
                    :ref="(el) => (fileInputRefs.cm05.value = el)"
                    class="d-none"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    @change="(e) => onFileChange('cm05', e)"
                  />
                </div>
                <div v-if="getFileStatus('cm05').state !== 'empty'" class="file-status" :class="getFileStatus('cm05').state">
                  <svg v-if="getFileStatus('cm05').state === 'success'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7 14C10.8661 14 14 10.8661 14 7C14 3.1339 10.8661 0 7 0C3.1339 0 0 3.1339 0 7C0 10.8661 3.1339 14 7 14ZM6.81956 9.83111L10.7084 5.16444L9.51378 4.16889L6.16933 8.18144L4.43878 6.45011L3.339 7.54989L5.67233 9.88322L6.27433 10.4852L6.81956 9.83111Z" fill="currentColor"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M7 0C10.8661 0 14 3.1339 14 7C14 10.8661 10.8661 14 7 14C3.1339 14 0 10.8661 0 7C0 3.1339 3.1339 0 7 0ZM7 9.1C6.81435 9.1 6.6363 9.17375 6.50503 9.30503C6.37375 9.4363 6.3 9.61435 6.3 9.8C6.3 9.98565 6.37375 10.1637 6.50503 10.295C6.6363 10.4263 6.81435 10.5 7 10.5C7.18565 10.5 7.3637 10.4263 7.49497 10.295C7.62625 10.1637 7.7 9.98565 7.7 9.8C7.7 9.61435 7.62625 9.4363 7.49497 9.30503C7.3637 9.17375 7.18565 9.1 7 9.1ZM7 2.8C6.82855 2.80002 6.66306 2.86297 6.53494 2.9769C6.40681 3.09083 6.32496 3.24782 6.3049 3.4181L6.3 3.5V7.7C6.3002 7.87842 6.36852 8.05002 6.49099 8.17976C6.61347 8.30949 6.78087 8.38756 6.95898 8.39802C7.13709 8.40848 7.31247 8.35053 7.44928 8.23601C7.5861 8.1215 7.67403 7.95907 7.6951 7.7819L7.7 7.7V3.5C7.7 3.31435 7.62625 3.1363 7.49497 3.00503C7.3637 2.87375 7.18565 2.8 7 2.8Z" fill="#FF4D4D"/>
</svg>
                  <span class="status-text">{{ getFileStatus('cm05').message }}</span>
                </div>
              </div>
            </div>

            <div class="file-item">
              <div class="form-label fw-bold">*Ingresos brutos</div>
              <div class="file-row">
                <div class="file-field">
                  <input class="file-name" type="text" :value="fileName('ingresos_brutos') || 'Nombredelarchivo.jpg'" readonly />
                  <button class="file-btn" type="button" @click="triggerFile('ingresos_brutos')">Seleccionar</button>
                  <input
                    id="file-ingresos-brutos"
                    :ref="(el) => (fileInputRefs.ingresos_brutos.value = el)"
                    class="d-none"
                    type="file"
                    accept=".jpg,.jpeg,.png,.pdf"
                    @change="(e) => onFileChange('ingresos_brutos', e)"
                  />
                </div>
                <div v-if="getFileStatus('ingresos_brutos').state !== 'empty'" class="file-status" :class="getFileStatus('ingresos_brutos').state">
                  <svg v-if="getFileStatus('ingresos_brutos').state === 'success'" xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true" focusable="false">
                    <path fill-rule="evenodd" clip-rule="evenodd" d="M7 14C10.8661 14 14 10.8661 14 7C14 3.1339 10.8661 0 7 0C3.1339 0 0 3.1339 0 7C0 10.8661 3.1339 14 7 14ZM6.81956 9.83111L10.7084 5.16444L9.51378 4.16889L6.16933 8.18144L4.43878 6.45011L3.339 7.54989L5.67233 9.88322L6.27433 10.4852L6.81956 9.83111Z" fill="currentColor"/>
                  </svg>
                  <svg v-else xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 14 14" fill="none">
  <path d="M7 0C10.8661 0 14 3.1339 14 7C14 10.8661 10.8661 14 7 14C3.1339 14 0 10.8661 0 7C0 3.1339 3.1339 0 7 0ZM7 9.1C6.81435 9.1 6.6363 9.17375 6.50503 9.30503C6.37375 9.4363 6.3 9.61435 6.3 9.8C6.3 9.98565 6.37375 10.1637 6.50503 10.295C6.6363 10.4263 6.81435 10.5 7 10.5C7.18565 10.5 7.3637 10.4263 7.49497 10.295C7.62625 10.1637 7.7 9.98565 7.7 9.8C7.7 9.61435 7.62625 9.4363 7.49497 9.30503C7.3637 9.17375 7.18565 9.1 7 9.1ZM7 2.8C6.82855 2.80002 6.66306 2.86297 6.53494 2.9769C6.40681 3.09083 6.32496 3.24782 6.3049 3.4181L6.3 3.5V7.7C6.3002 7.87842 6.36852 8.05002 6.49099 8.17976C6.61347 8.30949 6.78087 8.38756 6.95898 8.39802C7.13709 8.40848 7.31247 8.35053 7.44928 8.23601C7.5861 8.1215 7.67403 7.95907 7.6951 7.7819L7.7 7.7V3.5C7.7 3.31435 7.62625 3.1363 7.49497 3.00503C7.3637 2.87375 7.18565 2.8 7 2.8Z" fill="#FF4D4D"/>
</svg>
                  <span class="status-text">{{ getFileStatus('ingresos_brutos').message }}</span>
                </div>
              </div>
            </div>
          </template>
        </div>

        <p class="files-hint">*Adjuntar archivos JPG o PDF. Peso máximo 2 MB.</p>

        <label class="pred-row" for="pred">
          <input v-model="form.isPredeterminada" class="pred-check" type="checkbox" id="pred" />
          <span class="pred-text">Predeterminada</span>
        </label>

        <div class="d-flex align-items-center justify-content-between mt-4">
          <button v-if="isEdit" class="btn btn-delete" type="button" @click="openDeleteModal">
            Eliminar
          </button>
          <div class="d-flex justify-content-end">
            <button class="btn btn-cancel me-2" type="button" @click="handleCancel">Cancelar</button>
            <button class="btn btn-save" type="button" @click="handleSave">Guardar</button>
          </div>
        </div>
      </div>
    </div>

    <div
      ref="deleteModalEl"
      class="modal fade"
      id="facturacionDeleteModal"
      tabindex="-1"
      aria-labelledby="facturacionDeleteModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content">
          <div class="modal-header">
            <h5 class="modal-title" id="facturacionDeleteModalLabel">Eliminar perfil de facturación</h5>
            <button type="button" class="btn-close" aria-label="Close" @click="closeDeleteModal" />
          </div>
          <div class="modal-body">
            ¿Querés eliminar este perfil de facturación? Esta acción no podrá deshacerse.
          </div>
          <div class="modal-footer">
            <button type="button" class="btn btn-cancel" @click="closeDeleteModal" :disabled="isDeleting">Cancelar</button>
            <button type="button" class="btn btn-delete" @click="confirmDelete" :disabled="isDeleting">
              {{ isDeleting ? 'Eliminando...' : 'Eliminar' }}
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.facturacion-nuevo-page {
  background-color: #f8f9fa;
  min-height: 100vh;
  font-family: 'Roboto', sans-serif;
}

.facturacion-card {
  background: #fff;
  border-radius: 6px;
  padding: 65px;
  max-width: 1110px;
  margin: 0 auto;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.05);
}

.iva-title {
  margin: 0 0 10px 0;
  font-size: 13px;
  font-weight: 700;
  color: #000;
}

.iva-options {
  display: flex;
  gap: 25px;
  align-items: center;
}

.iva-option {
  display: inline-flex;
  align-items: center;
  gap: 0;
  cursor: pointer;
  user-select: none;
}

.iva-radio {
  width: 24px;
  height: 24px;
  margin: 0 8px 0 0;
  accent-color: #375DBF;
}

.iva-tag {
  display: inline-flex;
  align-items: center;
  border-radius: 3px;
  padding: 5px 9px;
  font-size: 14px;
  font-weight: 700;
  letter-spacing: 0;
  white-space: nowrap;
}

.iva-tag--consumidor {
  background: #f5c8ac;
  color: #7a3b0c;
}

.iva-tag--responsable {
  background: #d0eafb;
  color: #1c5f8a;
}

.grid-row {
  margin-top: 25px;
}

.two-col {
  display: grid;
  grid-template-columns: 460px 1fr;
  column-gap: 60px;
  align-items: start;
}

.left-group {
  display: grid;
  grid-template-columns: 214px 219px;
  gap: 26px;
}

.right-group {
  display: grid;
  grid-template-columns: 216px 220px;
  gap: 24px;
}

.field {
  display: flex;
  flex-direction: column;
  gap: 9px;
}

.custom-input {
  border: 1px solid #bbb;
  border-radius: 4px !important;
  height: 41px;
  padding: 10px 16px;
  font-size: 13px;
  background-color: #fff;
}

.custom-input:focus {
  border-color: #375DBF;
  box-shadow: 0 0 0 0.2rem rgba(55, 93, 191, 0.15);
  outline: none;
}

.form-label {
  font-size: 13px;
  margin-bottom: 8px;
  color: #000;
}

.files-block {
  margin-top: 25px;
  display: flex;
  flex-direction: column;
  gap: 25px;
}

.file-item {
  width: 100%;
}

.file-field {
  display: flex;
  width: 670px;
  max-width: 100%;
}

.file-row {
  display: flex;
  align-items: center;
  gap: 18px;
}

.file-status {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  white-space: nowrap;
  padding: 5px 10px;
  border-radius: 5px;
  border: 1px solid currentColor;
}

.file-status.success {
  color: #006C4C;
  border-color: #006C4C;
}

.file-status.error {
  color: #FF4D4D;
  border-color: #FF4D4D;
}

.status-dot {
  width: 8px;
  height: 8px;
  border-radius: 999px;
  background: #198754;
}

.file-status.error .status-dot {
  background: #dc3545;
}

.file-name {
  flex: 1;
  height: 41px;
  border: 1px solid #bbb;
  border-right: none;
  border-radius: 4px !important;
  padding: 10px 16px;
  font-size: 13px;
  color: #000;
  background: #fff;
}

.file-btn {
  width: 162px;
  height: 41px;
  background: #6b7280;
  color: #fff;
  border: 1px solid #6b7280;
  border-radius: 0 2px 2px 0;
  font-size: 15px;
  font-weight: 700;
}

.files-hint {
  margin: 25px 0 0 0;
  font-size: 13px;
  color: #000;
  text-align: left;
}

.pred-row {
  margin-top: 25px;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  cursor: pointer;
  user-select: none;
}

.pred-check {
  width: 16px;
  height: 16px;
  accent-color: #375DBF;
}

.pred-text {
  font-size: 15px;
  font-weight: 500;
  color: #000;
}

.btn-cancel {
  background-color: #e9ecef;
  border: 1px solid #e9ecef;
  color: #374151;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
}

.btn-save {
  background-color: #375DBF;
  border: 1px solid #375DBF;
  color: #fff;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
}

.btn-save:hover {
  background-color: #2d4a99;
  border-color: #2d4a99;
}

.btn-delete {
  background-color: #b91c1c;
  border: 1px solid #b91c1c;
  color: #fff;
  padding: 10px 24px;
  border-radius: 4px;
  font-size: 16px;
  font-weight: bold;
}

.btn-delete:hover {
  background-color: #991b1b;
  border-color: #991b1b;
}


@media (max-width: 992px) {
  .facturacion-card {
    padding: 32px;
  }

  .two-col {
    grid-template-columns: 1fr;
    row-gap: 25px;
  }

  .left-group,
  .right-group {
    grid-template-columns: 1fr;
  }
}
</style>
